import clsx from 'clsx';
import type {
  SignalFieldMappings,
  SignalVariant,
} from '@arcanum-hq/shared-types';
import { CapitalStackBar, type CapitalStackSlice } from './CapitalStackBar';
import {
  ProjectUsesTable,
  type ProjectUseCategory,
  type ProjectUseRow,
} from './ProjectUsesTable';
import {
  ProjectSourcesBar,
  type ProjectSourceSlice,
  type ProjectSourceTier,
} from './ProjectSourcesBar';
import {
  useSignalFieldResolver,
  type SignalFieldResolverContext,
} from '@/hooks/use-signal-field-resolver';
import { Skeleton } from '@/components/ui/Skeleton';

// deal-signal-overhaul Phase 4 Session 4 (2026-05-12) + Phase 5 ND-parity
// restore (2026-05-12).
//
// Zone 6 of the Signal page — variant-aware. Operating-asset variant renders
// a horizontal stacked-bar (Senior / Subordinate / Equity). New-development
// variant renders TWO panels Lovable-style: Project Uses (Land / Hard / Soft /
// Financing / Total + per-unit math) on the LEFT and Project Sources (Debt
// stacked bar over Equity stacked bar with Total Development Cost panel) on
// the RIGHT. Callers control which panel renders via the `slot` prop —
// `<SignalNewDevelopment>` mounts it twice inside a flex row (mirrors
// `<SignalOperatingAsset>`'s `BeforeAfterComparison + CapitalStackVisual`
// row at SignalOperatingAsset.tsx:172-190). `slot` is ignored when
// `variant === 'operating-asset'` — op-asset has only one render shape.
//
// All variants consume schema-driven `signalFieldMappings` via
// `useSignalFieldResolver`.

interface OpAssetSliceConfig {
  key: keyof SignalFieldMappings;
  label: string;
  tier: 'senior' | 'mezz' | 'equity';
  /** Tailwind background token (used by the legacy fallback bar). */
  color: string;
}

const OP_ASSET_SLICES: ReadonlyArray<OpAssetSliceConfig> = [
  {
    key: 'capitalStackSeniorLoan',
    label: 'Senior Debt',
    tier: 'senior',
    color: 'bg-purple',
  },
  {
    key: 'capitalStackSubordinate',
    label: 'Sub Debt',
    tier: 'mezz',
    color: 'bg-purple',
  },
  {
    key: 'capitalStackEquity',
    label: 'Equity',
    tier: 'equity',
    color: 'bg-blue',
  },
];

interface NewDevUseConfig {
  category: ProjectUseCategory;
  key: keyof SignalFieldMappings;
  /**
   * Optional second key summed INTO this row. Soft Costs = TotalSoftCost +
   * FinancingSoftCost so the row equals DealSummary G10 (the cell the
   * cycle-resolution fix unfroze), not just the TotalSoftCost subtotal.
   */
  plusKey?: keyof SignalFieldMappings;
}

const NEW_DEV_USES: ReadonlyArray<NewDevUseConfig> = [
  { category: 'Land', key: 'usesLandCost' },
  { category: 'Hard', key: 'usesHardCosts' },
  { category: 'Soft', key: 'usesSoftCosts', plusKey: 'usesFinancingSoftCost' },
  { category: 'Financing', key: 'usesFinancingCosts' },
  { category: 'Total', key: 'usesTotalDevelopmentCost' },
];

interface NewDevSourceConfig {
  key: keyof SignalFieldMappings;
  label: string;
  tier: ProjectSourceTier;
}

// Mirrors DealSummary "Capital Stack Summary" Project Sources rows G14–G17.
// Rendered as proportional block panels (Senior purple / Incentives amber /
// Equity blue). Each panel renders ONLY when its amount is finite and > 0 —
// so the two incentive sources appear only when the deal actually has them.
const NEW_DEV_SOURCES: ReadonlyArray<NewDevSourceConfig> = [
  { key: 'capitalStackSeniorLoan', label: 'Senior Loan', tier: 'senior' },
  { key: 'capitalStackIncentive1', label: 'Incentive 1', tier: 'incentive' },
  { key: 'capitalStackIncentive2', label: 'Incentive 2', tier: 'incentive' },
  { key: 'capitalStackEquity', label: 'Equity', tier: 'equity' },
];

function toFiniteNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export interface CapitalStackVisualProps {
  context: SignalFieldResolverContext;
  variant: SignalVariant;
  /**
   * New-development only — picks which half of the Lovable side-by-side
   * row this mount renders. Defaults to `'uses'` (the Project Uses table)
   * for back-compat with callers that only want the uses panel.
   * Ignored when `variant === 'operating-asset'`.
   */
  slot?: 'uses' | 'sources';
  className?: string;
}

/**
 * Variant-aware capital stack surface.
 *
 * 4-state contract:
 *   - loading: skeleton block while the resolver is hydrating.
 *   - error: caught at the route level (TanStack Query boundary).
 *   - empty: caller-primitive empty state — `<CapitalStackBar>` and
 *     `<ProjectUsesTable>` each render a graceful "no data" affordance when
 *     all values are null.
 *   - success: full variant-specific render.
 */
export function CapitalStackVisual({
  context,
  variant,
  slot = 'uses',
  className,
}: CapitalStackVisualProps) {
  const resolver = useSignalFieldResolver(context);

  if (!resolver.isReady) {
    return (
      <div
        data-testid="capital-stack-visual"
        data-state="loading"
        data-variant={variant}
        className={clsx(
          'border border-panel-border rounded-[3px] bg-panel-bg p-4',
          className
        )}
      >
        <Skeleton variant="block" className="h-16" />
      </div>
    );
  }

  if (variant === 'operating-asset') {
    const slices: CapitalStackSlice[] = [];
    for (const cfg of OP_ASSET_SLICES) {
      const amount = toFiniteNumber(resolver.resolve(cfg.key));
      if (amount != null && amount > 0) {
        slices.push({
          label: cfg.label,
          amount,
          tier: cfg.tier,
          color: cfg.color,
        });
      }
    }
    const totalCap = toFiniteNumber(
      resolver.resolve('capitalStackTotalCapitalization')
    );
    return (
      <div
        data-testid="capital-stack-visual"
        data-state="ready"
        data-variant="operating-asset"
        className={className}
      >
        <CapitalStackBar
          slices={slices}
          total={totalCap ?? undefined}
          totalLabel="Total Capitalization"
        />
      </div>
    );
  }

  // new-development
  if (slot === 'sources') {
    // Project Sources block — Lovable-aligned panels mirroring the DealSummary
    // "Capital Stack Summary" Project Sources rows (Senior Loan / Incentive 1 /
    // Incentive 2 / Equity) over a Total Sources panel. INCENTIVES RENDER ONLY
    // WHEN THEY EXIST — a source slice is included only when its amount is
    // finite and > 0. DUAL DENOMINATOR (preserved): each top panel's % is
    // amount / TotalSources (panels sum to 100%); the bottom Total Sources
    // panel shows the headline % = TotalSources / TDC, the only place it can
    // exceed 100% when incentives over-fund the stack.
    const tdc = toFiniteNumber(
      resolver.resolve('capitalStackTotalCapitalization')
    );
    const slices: ProjectSourceSlice[] = [];
    let totalSources = 0;
    for (const cfg of NEW_DEV_SOURCES) {
      const amount = toFiniteNumber(resolver.resolve(cfg.key));
      if (amount == null || amount <= 0) continue; // only sources that exist
      totalSources += amount;
      slices.push({ label: cfg.label, amount, tier: cfg.tier });
    }
    return (
      <div
        data-testid="capital-stack-visual"
        data-state="ready"
        data-variant="new-development"
        data-slot="sources"
        className={className}
      >
        <ProjectSourcesBar
          slices={slices}
          total={totalSources}
          totalPercent={tdc != null && tdc > 0 ? totalSources / tdc : NaN}
          title="Project Sources"
          totalLabel="Total Sources"
        />
      </div>
    );
  }

  const totalDevCost = toFiniteNumber(
    resolver.resolve('usesTotalDevelopmentCost')
  );
  const unitCount = toFiniteNumber(resolver.resolve('snapshotUnits'));
  const uses: ProjectUseRow[] = [];
  for (const cfg of NEW_DEV_USES) {
    const base = toFiniteNumber(resolver.resolve(cfg.key));
    if (base == null) continue;
    const extra = cfg.plusKey
      ? toFiniteNumber(resolver.resolve(cfg.plusKey))
      : null;
    const amount = base + (extra ?? 0);
    const percentTDC =
      totalDevCost != null && totalDevCost > 0 ? amount / totalDevCost : NaN;
    const perUnit =
      unitCount != null && unitCount > 0 ? amount / unitCount : NaN;
    uses.push({ category: cfg.category, amount, percentTDC, perUnit });
  }

  return (
    <div
      data-testid="capital-stack-visual"
      data-state="ready"
      data-variant="new-development"
      data-slot="uses"
      className={className}
    >
      <ProjectUsesTable uses={uses} />
    </div>
  );
}
